import type { DragEndEvent } from '@dnd-kit/core';
import type { MenuTheme, StoreInfo } from '../../api/menus';

export interface MenuContentCategory {
  id: string;
  name: string;
  description: string | null;
  image_url?: string | null;
  is_visible?: boolean;
  items: MenuContentItem[];
}

export interface MenuContentBadgeDef {
  key: string;
  label: string;
  color: string;
}

export interface MenuContentItemOption {
  name: string;
  price: number;
}

export interface MenuContentItem {
  id: string;
  name: string;
  description: string | null;
  price: number;
  image_url: string | null;
  badges: string[];
  spicy_level?: number | null;
  options?: MenuContentItemOption[] | null;
  is_sold_out: boolean;
  is_visible?: boolean;
}

export interface MenuContentProps {
  name: string;
  description: string | null;
  currency: string;
  categories: MenuContentCategory[];
  theme: MenuTheme | null;
  storeInfo?: StoreInfo;
  badgeDefinitions?: MenuContentBadgeDef[];
  headerImageUrl?: string | null;
  availableLanguages: string[];
  currentLanguage: string;
  onLanguageChange?: (lang: string) => void;
  onCategoryClick?: (categoryId: string) => void;
  onItemClick?: (itemId: string) => void;
  onAddCategory?: () => void;
  onAddItem?: (categoryId: string) => void;
  onReorderCategories?: (event: DragEndEvent) => void;
  onReorderItems?: (categoryId: string, event: DragEndEvent) => void;
  onTitleClick?: () => void;
  onStoreInfoClick?: () => void;
  onHeaderImageClick?: () => void;
  page?: 1 | 2;
  onPageChange?: (page: 1 | 2) => void;
  // Quick edit
  onQuickEditItem?: (itemId: string, updates: { name?: string; description?: string; price?: number; is_visible?: boolean; is_sold_out?: boolean }) => void;
  onQuickEditItemImage?: (itemId: string) => void;
  onQuickEditCategory?: (categoryId: string, updates: { name?: string; is_visible?: boolean }) => void;
  onQuickEditCategoryImage?: (categoryId: string) => void;
  onQuickEditTitle?: (name: string) => void;
  onQuickEditDescription?: (description: string) => void;
  onQuickEditHeaderImage?: () => void;
}

export const EDIT_HOVER = {
  backgroundColor: 'rgba(59, 130, 246, 0.08)',
  borderColor: 'rgba(59, 130, 246, 0.5)',
};

export const EDIT_TRANSITION = 'border-color 0.15s, background-color 0.15s';

export const NATIVE_LANGUAGE_NAMES: Record<string, string> = {
  ar: 'العربية',
  zh: '中文',
  nl: 'Nederlands',
  en: 'English',
  fr: 'Français',
  de: 'Deutsch',
  hi: 'हिन्दी',
  it: 'Italiano',
  ja: '日本語',
  ko: '한국어',
  pt: 'Português',
  ru: 'Русский',
  es: 'Español',
  tr: 'Türkçe',
  uk: 'Українська',
  vi: 'Tiếng Việt',
};

export const VIEW_STRINGS: Record<string, Record<string, string>> = {
  en: { back: 'Back', search: 'Search items...', noResults: 'No items found', soldOut: 'Sold Out', storeHours: 'Store Hours', address: 'Address', wifi: 'Wifi', instagram: 'Instagram', moreInfo: 'more info', storeInfo: 'Store Info' },
  tr: { back: 'Geri', search: 'Ürün ara...', noResults: 'Ürün bulunamadı', soldOut: 'Tükendi', storeHours: 'Çalışma Saatleri', address: 'Adres', wifi: 'Wifi', instagram: 'Instagram', moreInfo: 'daha fazla', storeInfo: 'Mağaza Bilgisi' },
  es: { back: 'Volver', search: 'Buscar artículos...', noResults: 'No se encontraron artículos', soldOut: 'Agotado', storeHours: 'Horario', address: 'Dirección', wifi: 'Wifi', instagram: 'Instagram', moreInfo: 'más info', storeInfo: 'Info de la tienda' },
  fr: { back: 'Retour', search: 'Rechercher...', noResults: 'Aucun article trouvé', soldOut: 'Épuisé', storeHours: 'Horaires', address: 'Adresse', wifi: 'Wifi', instagram: 'Instagram', moreInfo: "plus d'infos", storeInfo: 'Infos du magasin' },
  de: { back: 'Zurück', search: 'Artikel suchen...', noResults: 'Keine Artikel gefunden', soldOut: 'Ausverkauft', storeHours: 'Öffnungszeiten', address: 'Adresse', wifi: 'WLAN', instagram: 'Instagram', moreInfo: 'mehr Info', storeInfo: 'Geschäftsinfo' },
  it: { back: 'Indietro', search: 'Cerca articoli...', noResults: 'Nessun articolo trovato', soldOut: 'Esaurito', storeHours: 'Orari', address: 'Indirizzo', wifi: 'Wifi', instagram: 'Instagram', moreInfo: 'altre info', storeInfo: 'Info negozio' },
  pt: { back: 'Voltar', search: 'Pesquisar itens...', noResults: 'Nenhum item encontrado', soldOut: 'Esgotado', storeHours: 'Horário', address: 'Endereço', wifi: 'Wifi', instagram: 'Instagram', moreInfo: 'mais info', storeInfo: 'Info da loja' },
  ar: { back: 'رجوع', search: 'ابحث...', noResults: 'لم يتم العثور على عناصر', soldOut: 'نفذ', storeHours: 'ساعات العمل', address: 'العنوان', wifi: 'واي فاي', instagram: 'Instagram', moreInfo: 'المزيد', storeInfo: 'معلومات المتجر' },
  zh: { back: '返回', search: '搜索菜品...', noResults: '未找到菜品', soldOut: '售罄', storeHours: '营业时间', address: '地址', wifi: 'Wifi', instagram: 'Instagram', moreInfo: '更多信息', storeInfo: '店铺信息' },
  ja: { back: '戻る', search: '検索...', noResults: '見つかりません', soldOut: '売切', storeHours: '営業時間', address: '住所', wifi: 'Wifi', instagram: 'Instagram', moreInfo: '詳細', storeInfo: '店舗情報' },
  ko: { back: '뒤로', search: '검색...', noResults: '항목을 찾을 수 없습니다', soldOut: '품절', storeHours: '영업시간', address: '주소', wifi: 'Wifi', instagram: 'Instagram', moreInfo: '더보기', storeInfo: '매장 정보' },
  ru: { back: 'Назад', search: 'Поиск...', noResults: 'Ничего не найдено', soldOut: 'Нет в наличии', storeHours: 'Часы работы', address: 'Адрес', wifi: 'Wi-Fi', instagram: 'Instagram', moreInfo: 'подробнее', storeInfo: 'Информация' },
  hi: { back: 'वापस', search: 'खोजें...', noResults: 'कोई आइटम नहीं मिला', soldOut: 'बिक गया', storeHours: 'समय', address: 'पता', wifi: 'वाईफाई', instagram: 'Instagram', moreInfo: 'अधिक जानकारी', storeInfo: 'स्टोर जानकारी' },
  nl: { back: 'Terug', search: 'Zoek items...', noResults: 'Geen items gevonden', soldOut: 'Uitverkocht', storeHours: 'Openingstijden', address: 'Adres', wifi: 'Wifi', instagram: 'Instagram', moreInfo: 'meer info', storeInfo: 'Winkelinfo' },
  uk: { back: 'Назад', search: 'Пошук...', noResults: 'Нічого не знайдено', soldOut: 'Розпродано', storeHours: 'Години роботи', address: 'Адреса', wifi: 'Wi-Fi', instagram: 'Instagram', moreInfo: 'детальніше', storeInfo: 'Інформація' },
  vi: { back: 'Quay lại', search: 'Tìm kiếm...', noResults: 'Không tìm thấy', soldOut: 'Hết hàng', storeHours: 'Giờ mở cửa', address: 'Địa chỉ', wifi: 'Wifi', instagram: 'Instagram', moreInfo: 'thêm', storeInfo: 'Thông tin cửa hàng' },
};

export const CURRENCY_LOCALE_MAP: Record<string, string> = {
  USD: 'en-US', EUR: 'de-DE', GBP: 'en-GB', TRY: 'tr-TR',
  JPY: 'ja-JP', KRW: 'ko-KR', CNY: 'zh-CN', INR: 'en-IN',
  BRL: 'pt-BR', RUB: 'ru-RU', MXN: 'es-MX', ARS: 'es-AR',
  COP: 'es-CO', CLP: 'es-CL', PEN: 'es-PE', AED: 'ar-AE',
  SAR: 'ar-SA', EGP: 'ar-EG', ILS: 'he-IL', PLN: 'pl-PL',
  SEK: 'sv-SE', NOK: 'nb-NO', DKK: 'da-DK', CHF: 'de-CH',
  CAD: 'en-CA', AUD: 'en-AU', NZD: 'en-NZ', SGD: 'en-SG',
  HKD: 'zh-HK', TWD: 'zh-TW', THB: 'th-TH', VND: 'vi-VN',
  IDR: 'id-ID', MYR: 'ms-MY', PHP: 'en-PH', ZAR: 'en-ZA',
  NGN: 'en-NG', KES: 'en-KE', UAH: 'uk-UA', CZK: 'cs-CZ',
  HUF: 'hu-HU', RON: 'ro-RO', BGN: 'bg-BG', HRK: 'hr-HR',
  RSD: 'sr-RS', GEL: 'ka-GE', AMD: 'hy-AM', AZN: 'az-AZ',
  KZT: 'kk-KZ', UZS: 'uz-UZ', PKR: 'ur-PK', BDT: 'bn-BD',
  LKR: 'si-LK', MMK: 'my-MM', KHR: 'km-KH', LAK: 'lo-LA',
};

export const formatPrice = (price: number, currency: string): string => {
  const curr = currency || 'USD';
  const locale = CURRENCY_LOCALE_MAP[curr] || 'en-US';
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: curr,
    minimumFractionDigits: 2,
  }).format(price);
};

export const getHoverBg = (bg: string, text: string): string => {
  const parse = (hex: string) => {
    const h = hex.replace('#', '');
    return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
  };
  try {
    const [br, bg2, bb] = parse(bg);
    const [tr, tg, tb] = parse(text);
    const mix = 0.07;
    const r = Math.round(br + (tr - br) * mix);
    const g = Math.round(bg2 + (tg - bg2) * mix);
    const b = Math.round(bb + (tb - bb) * mix);
    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
  } catch {
    return bg;
  }
};
